import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import Supercluster from "supercluster";
import { apiV2 } from "../../../api/axiosConfig";
import { useMapSettings } from "../../operational-map/context/useMapSettings";
import type { MapFacility, FacilityPopupState } from "./useFacilitiesLayer";

// --- WebGL helpers ---

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) ?? "Shader compile error");
    }
    return shader;
}

function buildProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vert);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, frag);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    return prog;
}

function loadTexture(gl: WebGLRenderingContext, src: string, onLoad?: () => void): WebGLTexture {
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    const img = new Image();
    img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        const ext = gl.getExtension("EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("MOZ_EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
        if (ext) gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, 4);
        onLoad?.();
    };
    img.src = src;
    return tex;
}

// --- Shaders ---

const ICON_VERT = `
    attribute vec2 a_position;
    attribute float a_highlighted;
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        gl_PointSize = a_highlighted > 0.5 ? 32.0 : 24.0;
    }
`;

const ICON_FRAG = `
    #extension GL_EXT_shader_texture_lod : enable
    precision mediump float;
    uniform sampler2D u_texture;
    void main() {
        vec2  uv   = gl_PointCoord - 0.5;
        float dist = length(uv);

        float sm        = 0.04;
        float fillR     = 0.5;
        float iconScale = 0.71;

        if (dist > fillR) discard;

        // Purple filled circle, antialiased edge (#280e70)
        float fillAlpha = smoothstep(fillR, fillR - sm, dist);
        vec4 col = vec4(0.157, 0.055, 0.439, fillAlpha);

        // Icon sampled from scaled-down UV region
        vec2 iconUV = (gl_PointCoord - 0.5) / iconScale + 0.5;
        if (iconUV.x >= 0.0 && iconUV.x <= 1.0 && iconUV.y >= 0.0 && iconUV.y <= 1.0) {
            #ifdef GL_EXT_shader_texture_lod
                vec4 iconColor = texture2DLodEXT(u_texture, iconUV, 0.0);
            #else
                vec4 iconColor = texture2D(u_texture, iconUV, -1.0);
            #endif
            col.rgb = iconColor.rgb + col.rgb * (1.0 - iconColor.a);
        }

        if (col.a < 0.01) discard;
        gl_FragColor = vec4(col.rgb * col.a, col.a);
    }
`;

const FACILITY_ICON = "/assets/mapicons/facility_transparent.png";
const CLICK_RADIUS  = 30;
const CLUSTER_RADIUS = 50;
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_BG   = "#280e70";
const CLUSTER_TEXT = "#ffffff";

// --- Rendered point ---

type RenderedPoint = {
    x: number;
    y: number;
    facility?: MapFacility;
    isCluster: boolean;
    clusterID?: number;
    count?: number;
};

// --- Overlay factory ---

type DrawPoint = { lng: number; lat: number; facility?: MapFacility; isCluster: boolean; clusterID?: number; count?: number };

function createLegacyFacilitiesOverlay(
    map: google.maps.Map,
    onRenderedPoints: (pts: RenderedPoint[]) => void,
    onRedraw: () => void,
) {
    const glCanvas = document.createElement("canvas");
    glCanvas.style.position = "absolute";
    glCanvas.style.top = "0";
    glCanvas.style.left = "0";
    glCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(glCanvas);

    const clusterCanvas = document.createElement("canvas");
    clusterCanvas.style.position = "absolute";
    clusterCanvas.style.top = "0";
    clusterCanvas.style.left = "0";
    clusterCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(clusterCanvas);

    const gl = glCanvas.getContext("webgl", { alpha: true, premultipliedAlpha: true })!;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const prog = buildProgram(gl, ICON_VERT, ICON_FRAG);
    const iconTex = loadTexture(gl, FACILITY_ICON, onRedraw);

    let isReady = false;
    const overlay = new (class extends google.maps.OverlayView {
        onAdd()    { isReady = true; }
        draw()     {}
        onRemove() { isReady = false; }
        getProjectionSafe(): google.maps.MapCanvasProjection | null {
            if (!isReady) return null;
            try { return this.getProjection(); } catch { return null; }
        }
    })();
    overlay.setMap(map);

    let drawPoints: DrawPoint[] = [];
    let hoveredIDs = new Set<string>();
    let rafId: number | null = null;

    function resizeCanvases(width: number, height: number, dpr: number) {
        for (const c of [glCanvas, clusterCanvas]) {
            c.style.width  = `${width}px`;
            c.style.height = `${height}px`;
            c.width  = width  * dpr;
            c.height = height * dpr;
        }
    }

    function draw() {
        const projection = overlay.getProjectionSafe();
        if (!projection) return;

        const mapDiv = map.getDiv() as HTMLElement;
        const dpr    = window.devicePixelRatio || 1;
        const width  = mapDiv.offsetWidth;
        const height = mapDiv.offsetHeight;
        if (!width || !height) return;

        resizeCanvases(width, height, dpr);

        gl.viewport(0, 0, glCanvas.width, glCanvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const ctx2d = clusterCanvas.getContext("2d")!;
        ctx2d.clearRect(0, 0, clusterCanvas.width, clusterCanvas.height);
        ctx2d.scale(dpr, dpr);

        const allPts: RenderedPoint[] = [];

        if (drawPoints.length === 0) {
            ctx2d.setTransform(1, 0, 0, 1, 0, 0);
            onRenderedPoints(allPts);
            return;
        }

        const individuals = drawPoints.filter((dp) => !dp.isCluster);
        const clusters    = drawPoints.filter((dp) =>  dp.isCluster);

        // --- Draw individual icons via WebGL ---
        const positions: number[] = [];
        const highlighted: number[] = [];
        const pts: RenderedPoint[] = [];

        for (const dp of individuals) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const nx = (pixel.x / width) * 2 - 1;
            const ny = 1 - (pixel.y / height) * 2;
            positions.push(nx, ny);
            highlighted.push(dp.facility && hoveredIDs.has(dp.facility.id) ? 1.0 : 0.0);
            pts.push({ x: pixel.x, y: pixel.y, facility: dp.facility, isCluster: false });
        }

        if (positions.length > 0) {
            const count = positions.length / 2;
            gl.useProgram(prog);

            const posBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.DYNAMIC_DRAW);
            const aPosLoc = gl.getAttribLocation(prog, "a_position");
            gl.enableVertexAttribArray(aPosLoc);
            gl.vertexAttribPointer(aPosLoc, 2, gl.FLOAT, false, 0, 0);

            const hlBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, hlBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(highlighted), gl.DYNAMIC_DRAW);
            const aHlLoc = gl.getAttribLocation(prog, "a_highlighted");
            gl.enableVertexAttribArray(aHlLoc);
            gl.vertexAttribPointer(aHlLoc, 1, gl.FLOAT, false, 0, 0);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, iconTex);
            gl.uniform1i(gl.getUniformLocation(prog, "u_texture"), 0);
            gl.drawArrays(gl.POINTS, 0, count);

            allPts.push(...pts);
        }

        // --- Draw cluster bubbles via 2D canvas ---
        for (const dp of clusters) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const hovered = dp.clusterID !== undefined && hoveredIDs.has(`cluster_${dp.clusterID}`);
            const r = hovered ? 17 : 12;

            ctx2d.beginPath();
            ctx2d.arc(pixel.x, pixel.y, r, 0, Math.PI * 2);
            ctx2d.fillStyle = CLUSTER_BG;
            ctx2d.fill();

            ctx2d.fillStyle = CLUSTER_TEXT;
            ctx2d.font = `bold ${hovered ? 13 : 10}px sans-serif`;
            ctx2d.textAlign = "center";
            ctx2d.textBaseline = "middle";
            ctx2d.fillText(String(dp.count ?? 0), pixel.x, pixel.y + 1);

            allPts.push({ x: pixel.x, y: pixel.y, isCluster: true, clusterID: dp.clusterID, count: dp.count });
        }

        ctx2d.setTransform(1, 0, 0, 1, 0, 0);
        onRenderedPoints(allPts);
    }

    function startLoop() {
        if (rafId !== null) return;
        const loop = () => {
            draw();
            rafId = drawPoints.length > 0 ? requestAnimationFrame(loop) : null;
        };
        rafId = requestAnimationFrame(loop);
    }

    const mapListeners = [
        google.maps.event.addListener(map, "idle",           draw),
        google.maps.event.addListener(map, "bounds_changed", draw),
    ];

    return {
        setDrawPoints(next: DrawPoint[]) {
            drawPoints = next;
            if (next.length > 0) startLoop();
            else draw();
        },
        setHoveredIDs(ids: Set<string>) {
            hoveredIDs = ids;
            draw();
        },
        destroy() {
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            mapListeners.forEach((l) => google.maps.event.removeListener(l));
            overlay.setMap(null);
            if (glCanvas.parentNode) glCanvas.parentNode.removeChild(glCanvas);
            if (clusterCanvas.parentNode) clusterCanvas.parentNode.removeChild(clusterCanvas);
        },
    };
}

type LegacyFacilitiesOverlay = ReturnType<typeof createLegacyFacilitiesOverlay>;

// --- Supercluster ---

type FacilityClusterProps = { facility: MapFacility };

function buildClusterIndex(facilities: MapFacility[]): Supercluster<FacilityClusterProps> {
    const index = new Supercluster<FacilityClusterProps>({ radius: CLUSTER_RADIUS, maxZoom: CLUSTER_MAX_ZOOM });
    const points: GeoJSON.Feature<GeoJSON.Point, FacilityClusterProps>[] = facilities.map((f) => ({
        type: "Feature",
        properties: { facility: f },
        geometry: { type: "Point", coordinates: [f.coordinates[0], f.coordinates[1]] },
    }));
    index.load(points);
    return index;
}

function pushClusters(
    map: google.maps.Map | null,
    overlay: LegacyFacilitiesOverlay | null,
    index: Supercluster<FacilityClusterProps> | null,
    facilities: MapFacility[],
) {
    if (!overlay) return;
    if (!map || facilities.length === 0) { overlay.setDrawPoints([]); return; }
    if (!index) { overlay.setDrawPoints([]); return; }

    const zoom   = Math.floor(map.getZoom() ?? 0);
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const bbox: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];

    const clusters = index.getClusters(bbox, zoom);
    const drawPoints: DrawPoint[] = clusters.map((c) => {
        const [lng, lat] = c.geometry.coordinates;
        const props = c.properties as any;
        if (props.cluster) {
            return { lng, lat, isCluster: true, clusterID: props.cluster_id as number, count: props.point_count as number };
        }
        return { lng, lat, isCluster: false, facility: (props as FacilityClusterProps).facility };
    });

    overlay.setDrawPoints(drawPoints);
}

// --- Fetch ---

async function fetchLegacyFacilities(clientID: string | undefined): Promise<MapFacility[]> {
    const res = await apiV2.get("ops/facilities-legacy", {
        params: clientID ? { clientID } : {},
    });
    return res.data as MapFacility[];
}

// --- Hook return type ---

export type LegacyFacilitiesLayerResult = {
    popup: FacilityPopupState;
    setPopup: (s: FacilityPopupState) => void;
    getHitsAtPoint: (clientX: number, clientY: number) => { facilities: MapFacility[]; hoveredID: string | null };
    setHoveredIDs: (ids: Set<string>) => void;
    openPopupForFacilities: (facilities: MapFacility[], position: { x: number; y: number }) => void;
    legacyFacilityCount: number;
};

// --- Hook ---

export function useLegacyFacilitiesLayer(map: google.maps.Map | null): LegacyFacilitiesLayerResult {
    const { settings } = useMapSettings();
    const { clientID, showLegacyFacilities } = settings;

    const overlayRef      = useRef<LegacyFacilitiesOverlay | null>(null);
    const renderedRef     = useRef<RenderedPoint[]>([]);
    const clusterIndexRef = useRef<Supercluster<FacilityClusterProps> | null>(null);
    const facilitiesRef   = useRef<MapFacility[]>([]);

    const [popup, setPopup] = useState<FacilityPopupState>({ visible: false, position: { x: 0, y: 0 }, facilities: [] });

    const { data } = useQuery(
        ["legacy-facilities-layer", clientID, showLegacyFacilities],
        () => fetchLegacyFacilities(clientID),
        { refetchInterval: 60_000, enabled: showLegacyFacilities },
    );

    // --- Overlay lifecycle ---

    useEffect(() => {
        if (!map) return;

        const overlay = createLegacyFacilitiesOverlay(
            map,
            (pts) => { renderedRef.current = pts; },
            () => pushClusters(map, overlayRef.current, clusterIndexRef.current, facilitiesRef.current),
        );
        overlayRef.current = overlay;

        const listeners = [
            google.maps.event.addListener(map, "idle",           () => pushClusters(map, overlayRef.current, clusterIndexRef.current, facilitiesRef.current)),
            google.maps.event.addListener(map, "bounds_changed", () => pushClusters(map, overlayRef.current, clusterIndexRef.current, facilitiesRef.current)),
        ];

        return () => {
            overlay.destroy();
            overlayRef.current = null;
            listeners.forEach((l) => google.maps.event.removeListener(l));
        };
    }, [map]);

    // --- Push data when query result changes ---

    useEffect(() => {
        const facilities = data ?? [];
        facilitiesRef.current = facilities;
        clusterIndexRef.current = facilities.length > 0 ? buildClusterIndex(facilities) : null;
        pushClusters(map, overlayRef.current, clusterIndexRef.current, facilities);
    }, [data, map]);

    // --- Clear overlay when hidden ---

    useEffect(() => {
        if (!showLegacyFacilities) {
            facilitiesRef.current = [];
            clusterIndexRef.current = null;
            overlayRef.current?.setDrawPoints([]);
        }
    }, [showLegacyFacilities]);

    // --- Hit testing ---

    function getHitsAtPoint(clientX: number, clientY: number): { facilities: MapFacility[]; hoveredID: string | null } {
        if (!map) return { facilities: [], hoveredID: null };
        const mapDiv = map.getDiv() as HTMLElement;
        const rect = mapDiv.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        let closestDist = Infinity;
        let closestPt: RenderedPoint | null = null;

        for (const pt of renderedRef.current) {
            const dist = Math.hypot(pt.x - x, pt.y - y);
            if (dist <= CLICK_RADIUS && dist < closestDist) {
                closestDist = dist;
                closestPt = pt;
            }
        }

        if (!closestPt) return { facilities: [], hoveredID: null };

        if (closestPt.isCluster && closestPt.clusterID !== undefined && clusterIndexRef.current) {
            const leaves = clusterIndexRef.current.getLeaves(closestPt.clusterID, Infinity).map((l) => l.properties.facility);
            return { facilities: leaves, hoveredID: `cluster_${closestPt.clusterID}` };
        }

        return { facilities: closestPt.facility ? [closestPt.facility] : [], hoveredID: closestPt.facility?.id ?? null };
    }

    function openPopupForFacilities(facilities: MapFacility[], position: { x: number; y: number }) {
        if (facilities.length === 0) return;
        setPopup({ visible: true, position, facilities });
    }

    const setHoveredIDs = (ids: Set<string>) => {
        overlayRef.current?.setHoveredIDs(ids);
    };

    const legacyFacilityCount = data?.length ?? 0;

    return { popup, setPopup, getHitsAtPoint, setHoveredIDs, openPopupForFacilities, legacyFacilityCount };
}
